import { Check, Close } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { errorTranslationsWithFallback } from "components/ImportData/utils";
import type { TFileData } from "containers/BulkUpdatesContainer";
import { convertColumnIndexToTitle } from "containers/BulkUpdatesContainer/utils";
import { BodyHighlight } from "helpers/TypographyComponents";
import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";

import OptimizedTableCell from "../OptimizedTableCell";
import StyledButton from "../StyledButton";
import StyledTextField from "../StyledTextField/StyledTextField";
import { formatCellValue } from "./utils";

export const borderValue = "1px solid rgba(224, 224, 224, 1)";

interface ICorrectionTable {
    errors: TFileData;
    loadedData: TFileData;
    setLoadedData?: any;
    columns: ICorrectionColumnData[];
    /**
     * Floatkeys used in data. Only needed when not readOnly.
     */
    floatKeys?: string[];
    /**
     * ReadOnly when they are looking at anomalies.
     */
    readonly?: boolean;
}

export interface ICorrectionColumnData {
    key: string;
    title: string;
    originalIndex: number;
}

const CorrectionTable = ({
    errors,
    loadedData,
    setLoadedData,
    columns,
    floatKeys = [],
    readonly = false,
}: ICorrectionTable) => {
    const [t] = useTranslation();
    const mandatoryFields = ["surname", "ean13", "price", "releaseDate", "amount", "packageQuantity", "beneficiaryId"];
    const [deleteCheck, setDeleteCheck] = useState<null | number>(null);

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        index: number,
        key: string,
    ) => {
        setLoadedData((prev) => {
            return prev.map((item, itemIndex) => {
                if (itemIndex === index) {
                    if (floatKeys.includes(key)) {
                        return { ...item, [key]: e.target.value.replaceAll(",", ".") };
                    } else {
                        return { ...item, [key]: e.target.value };
                    }
                } else {
                    return item;
                }
            });
        });
    };

    const handleDeleteLine = (index: number) => {
        setLoadedData((prev) => prev.map((line, i) => (i === index ? {} : line)));
        setDeleteCheck(null);
    };

    const initialColumnsStyle = {
        padding: "8px",
        borderRight: borderValue,
        position: "sticky",
        background: "#F6F6F6",
        zIndex: 10,
    };

    return (
        <Fragment>
            <BodyHighlight style={{ maxWidth: "970px", margin: "25px auto 0px" }}>
                {!readonly && t("BulkUpdates.ConfirmationStep.CorrectErrors")}
            </BodyHighlight>
            <Paper
                sx={{
                    overflow: "hidden",
                    border: borderValue,
                    marginBottom: "0",
                    borderRadius: "0",
                    boxShadow: "none",
                }}
            >
                <TableContainer className="import-data-repairing-container">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        ...initialColumnsStyle,
                                        width: "30px",
                                        height: "45px",
                                        left: 0,
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        ...initialColumnsStyle,
                                        width: "30px",
                                        height: "45px",
                                        left: 73,
                                    }}
                                />
                                {columns.map((column) => {
                                    const columnNumber = column.originalIndex + 1;

                                    return (
                                        <TableCell
                                            sx={{
                                                padding: "8px",
                                                borderBottom: "none",
                                                fontWeight: "600",
                                                textAlign: "center",
                                                borderRight: borderValue,
                                                background: "#F6F6F6",
                                            }}
                                            key={"importTitles_column" + columnNumber}
                                        >
                                            {convertColumnIndexToTitle(columnNumber)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        ...initialColumnsStyle,
                                        width: "30px",
                                        height: "45px",
                                        left: 0,
                                    }}
                                />
                                <TableCell
                                    sx={{
                                        ...initialColumnsStyle,
                                        width: "30px",
                                        height: "45px",
                                        textAlign: "center",
                                        left: 73,
                                    }}
                                >
                                    1
                                </TableCell>
                                {columns.map((item) => {
                                    return (
                                        <TableCell
                                            sx={{
                                                whiteSpace: "nowrap",
                                                border: "1px solid rgba(224, 224, 224, 1)",
                                                width: "fit-content",
                                                background: "#F6F6F6",
                                                textAlign: "center",
                                            }}
                                            key={"importTitles_title" + item.originalIndex}
                                        >
                                            {item.title}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ maxHeight: "600px" }}>
                            {loadedData.map((item, index) => (
                                <TableRow
                                    key={"loadedData" + index}
                                    style={{
                                        display:
                                            errors.find((err) => err.fileLine - 2 === index) &&
                                            !!Object.keys(item).length
                                                ? ""
                                                : "none",
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            ...initialColumnsStyle,
                                            width: "30px",
                                            textAlign: "center",
                                            left: 0,
                                            fontWeight: "600",
                                            padding: "16px",
                                        }}
                                    >
                                        {deleteCheck === index ? (
                                            <div>
                                                <StyledButton
                                                    className="import-data-repairing-remove-btn red"
                                                    sx={{ background: "#F6F6F6" }}
                                                    onClick={() => {
                                                        setDeleteCheck(null);
                                                    }}
                                                >
                                                    <Close />
                                                </StyledButton>
                                                <StyledButton
                                                    className="import-data-repairing-remove-btn green"
                                                    sx={{ background: "#F6F6F6" }}
                                                    onClick={() => {
                                                        handleDeleteLine(index);
                                                    }}
                                                >
                                                    <Check />
                                                </StyledButton>
                                            </div>
                                        ) : (
                                            <StyledButton
                                                className="import-data-repairing-remove-btn red"
                                                sx={{
                                                    backgroundColor: "#F6F6F6",
                                                    ...(readonly && { pointerEvents: "none" }),
                                                }}
                                                onClick={() => {
                                                    setDeleteCheck(index);
                                                }}
                                            >
                                                {!readonly && <DeleteIcon sx={{ backgroundColor: "#F6F6F6" }} />}
                                            </StyledButton>
                                        )}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...initialColumnsStyle,
                                            fontWeight: "600",
                                            padding: "16px",
                                            textAlign: "center",
                                            left: 73,
                                        }}
                                    >
                                        {index + 2}
                                    </TableCell>
                                    {columns.map((column, indexKeys) => {
                                        const key = column.key;
                                        const errorKey = (i) => (i === "advancePayment" ? "initialBalance" : i);
                                        const errorExists =
                                            !!errors.find(
                                                (e) => key === errorKey(e.mappedKey) && e.fileLine - 2 === index,
                                            ) ||
                                            ([undefined, ""].includes(item[key]) && mandatoryFields.includes(key));
                                        const errorMessage = errors.find((e) => key === errorKey(e.mappedKey))?.message;

                                        return errorExists ? (
                                            <TableCell sx={{ padding: 0 }} key={"templateKeys" + index + indexKeys}>
                                                <StyledTextField
                                                    onChange={(e) => handleInputChange(e, index, key)}
                                                    sx={{
                                                        pointerEvents: readonly ? "none" : "auto",
                                                        ".MuiOutlinedInput-root": {
                                                            backgroundColor: "rgba(197, 83, 83, 0.2)",
                                                            paddingBlock: "5px",
                                                        },
                                                    }}
                                                    id={`field-${indexKeys}`}
                                                    fullWidth
                                                    defaultValue={item[key] ?? ""}
                                                    inputProps={{ readOnly: readonly }}
                                                    focused={false}
                                                />
                                                <span style={{ fontSize: "10px", marginLeft: "5px" }}>
                                                    {errorMessage ? errorTranslationsWithFallback(errorMessage) : ""}
                                                </span>
                                            </TableCell>
                                        ) : (
                                            <OptimizedTableCell
                                                sx={{ textAlign: "center", borderRight: borderValue }}
                                                key={"templateKeys" + index + indexKeys}
                                            >
                                                {formatCellValue(item[key])}
                                            </OptimizedTableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Fragment>
    );
};

export default CorrectionTable;
